export namespace LegacyThemes {
  export interface BoardStyles {
    [key: string]: string[];
  }

  export interface PieceStyles {
    [key: string]: { format: string; path: string; isPseudo3d?: boolean };
  }

  export interface Model {
    darkSquareColor: string;
    lightSquareColor: string;
    path: string;
    extension: string;
    highlightColor: string;
  }
}

/**
 * Short string piece names
 */
export const shortPieceNames = [
  'wp',
  'wn',
  'wb',
  'wr',
  'wq',
  'wk',
  'bp',
  'bn',
  'bb',
  'br',
  'bq',
  'bk',
];

/**
 * Board style schemes.
 */
export const defaultLegacyBoardStyles: LegacyThemes.BoardStyles = {
  // name:    [dark, light, path, extension, highlight]
  '8_bit': ['#6a9b41', '#f3f3f4', '8_bit', '.png', '#ffff00'],
  bases: ['#c26b38', '#efcca1', 'bases', '.jpg', '#f5cc2a'],
  blackwhite: ['#667188', '#282f3f', 'translucent', '.png', '#5b91b3'], // legacy
  blue: ['#4D6D92', '#ECECD7', 'blue', '.png', '#00a5ff'],
  brown: ['#B58863', '#F0D9B5', 'brown', '.png', '#ffff00'],
  bubblegum: ['#f9cdd3', '#fff3f3', 'bubblegum', '.png', '#de5d6f'],
  burled_wood: ['#895132', '#d9b088', 'burled_wood', '.jpg', '#ee9016'],
  checkers: ['#e3a6a9', '#999999', 'checkers', '.jpg', '#fae18a'],
  dark_wood: ['#8d675e', '#e7cdb2', 'dark_wood', '.jpg', '#cc9122'],
  dash: ['#6b3a27', '#bd9257', 'dash', '.jpg', '#eca722'],
  default: ['#779952', '#edeed1', 'green', '.png', '#ffff00'],
  glass: ['#282f3f', '#667188', 'glass', '.png', '#5b91b3'],
  graffiti: ['#b96f18', '#aeaeae', 'graffiti', '.jpg', '#f39011'],
  green: ['#779952', '#edeed1', 'green', '.png', '#ffff00'],
  grey: ['#aaaaaa', '#dcdcdc', 'light', '.png', '#a4b8c4'], // legacy
  icy_sea: ['#7a9db2', '#c5d5dc', 'icy_sea', '.jpg', '#5ed7f1'],
  light: ['#aaaaaa', '#dcdcdc', 'light', '.png', '#a4b8c4'],
  lolz: ['#909898', '#e0e9e9', 'lolz', '.jpg', '#a3becd'],
  marble: ['#706b66', '#c7bdaa', 'marble', '.jpg', '#f0db86'],
  marbleblue: ['#c2d7e2', '#efefef', 'marbleblue', '.jpg', '#65daf7'], // legacy
  marblebrown: ['#b96f18', '#aeaeae', 'graffiti', '.jpg', '#f39011'], // legacy
  marblegreen: ['#706b66', '#c7bdaa', 'marblegreen', '.jpg', '#f0db86'], // legacy
  metal: ['#6e6e6e', '#c9c9c9', 'metal', '.jpg', '#a3becd'],
  natural: ['#D08B18', '#FCE4B2', 'tan', '.png', '#f0e464'], // legacy
  nature: ['#8c976e', '#c3d1a4', 'translucent', '.png', '#eef396'], // legacy
  neon: ['#636363', '#b9b9b9', 'neon', '.png', '#6d90a6'],
  newspaper: ['#5a5956', '#5a5956', 'newspaper', '.jpg', '#99976e'],
  orange: ['#D08B18', '#FCE4B2', 'orange', '.png', '#ffff00'],
  overlay: ['#789ebd', '#4878a0', 'overlay', '.png', '#0d9acf'],
  parchment: ['#B58863', '#F0D9B5', 'parchment', '.jpg', '#d8cc66'],
  pink: ['#f9cdd3', '#fff3f3', 'bubblegum', '.png', '#de5d6f'], // legacy
  purple: ['#8877B7', '#EFEFEF', 'purple', '.png', '#7dacc9'],
  red: ['#BA5546', '#F0D8BF', 'red', '.png', '#f8f893'],
  sand: ['#b8a590', '#e5d3c4', 'sand', '.jpg', '#e2bc87'],
  sky: ['#c2d7e2', '#efefef', 'sky', '.png', '#65daf7'],
  stone: ['#666463', '#c8c3bd', 'stone', '.jpg', '#36525f'],
  tan: ['#D08B18', '#FCE4B2', 'tan', '.png', '#f7d84a'],
  tournament: ['#316549', '#ebece8', 'tournament', '.jpg', '#a4c25b'],
  translucent: ['#667188', '#282f3f', 'translucent', '.png', '#5b91b3'],
  walnut: ['#835f42', '#c0a684', 'walnut', '.jpg', '#d1a52d'],
  winboard: ['#316549', '#ebece8', 'tournament', '.jpg', '#a4c25b'], // legacy
  wood: ['#8d675e', '#e7cdb2', 'dark_wood', '.jpg', '#cc9122'], // legacy
  wooddark: ['#895132', '#d9b088', 'burled_wood', '.jpg', '#ee9016'], // legacy
  woodlight: ['#B58863', '#F0D9B5', 'parchment', '.jpg', '#d8cc66'], // legacy
  woodmid: ['#8d675e', '#e7cdb2', 'dark_wood', '.jpg', '#cc9122'], // legacy
  woodolive: ['#6e6e6e', '#c9c9c9', 'woodolive', '.jpg', '#a3becd'], // legacy
};

/**
 * Piece style schemes
 */
export const defaultLegacyPieceStyles: LegacyThemes.PieceStyles = {
  '3d_staunton': {
    format: 'png',
    isPseudo3d: true,
    path: '3d_staunton',
  },
  '3dchesskid': {
    format: 'png',
    isPseudo3d: true,
    path: '3d_chesskid',
  },
  '3dplastic': {
    format: 'png',
    isPseudo3d: true,
    path: '3d_plastic',
  },
  '3dwood': {
    format: 'png',
    isPseudo3d: true,
    path: '3d_wood',
  },
  '8_bit': {
    format: 'png',
    path: '8_bit',
  },
  alpha: {
    format: 'png',
    path: 'alpha',
  },
  bases: {
    format: 'png',
    path: 'bases',
  },
  blindfold: {
    format: 'png',
    path: 'blindfold',
  },
  book: {
    format: 'png',
    path: 'book',
  },
  bubblegum: {
    format: 'png',
    path: 'bubblegum',
  },
  cases: {
    format: 'png',
    path: 'cases',
  },
  classic: {
    format: 'png',
    path: 'classic',
  },
  club: {
    format: 'png',
    path: 'club',
  },
  condal: {
    format: 'png',
    path: 'condal',
  },
  dash: {
    format: 'png',
    path: 'dash',
  },
  game_room: {
    format: 'png',
    path: 'game_room',
  },
  glass: {
    format: 'png',
    path: 'glass',
  },
  gothic: {
    format: 'png',
    path: 'gothic',
  },
  graffiti: {
    format: 'png',
    path: 'graffiti',
  },
  icy_sea: {
    format: 'png',
    path: 'icy_sea',
  },
  light: {
    format: 'png',
    path: 'light',
  },
  lolz: {
    format: 'png',
    path: 'lolz',
  },
  luca: {
    format: 'png',
    path: 'luca',
  },
  marble: {
    format: 'png',
    path: 'marble',
  },
  maya: {
    format: 'png',
    path: 'maya',
  },
  metal: {
    format: 'png',
    path: 'metal',
  },
  modern2: {
    format: 'png',
    path: 'modern',
  },
  nature: {
    format: 'png',
    path: 'nature',
  },
  neo: {
    format: 'png',
    path: 'neo',
  },
  neo_wood: {
    format: 'png',
    path: 'neo_wood',
  },
  neon: {
    format: 'png',
    path: 'neon',
  },
  newspaper: {
    format: 'png',
    path: 'newspaper',
  },
  ocean: {
    format: 'png',
    path: 'ocean',
  },
  real3d: {
    format: 'png',
    path: 'neo',
  },
  sky: {
    format: 'png',
    path: 'sky',
  },
  space: {
    format: 'png',
    path: 'space',
  },
  tigers: {
    format: 'png',
    path: 'tigers',
  },
  tournament: {
    format: 'png',
    path: 'tournament',
  },
  vintage: {
    format: 'png',
    path: 'vintage',
  },
  wood: {
    format: 'png',
    path: 'wood',
  },
};

/**
 * Get the theme properties as an object.
 * Based on the legacy hardcoded themes.
 */
export function getLegacyThemeValues(boardStyle: string): LegacyThemes.Model | null {
  const themeArr = defaultLegacyBoardStyles[boardStyle];

  if (!themeArr) {
    return null;
  }

  const [darkSquareColor, lightSquareColor, path, extension, highlightColor] = themeArr;

  return {
    darkSquareColor,
    lightSquareColor,
    path,
    extension,
    highlightColor,
  };
}
